import { useCallback, useMemo, useState } from 'react';
import {
	getApiErrorMessage,
	useCreateFertilizationPlanReference,
	useDeleteFertilizationPlanReference,
	useGetFertilizationPlanReferences,
	useUpdateFertilizationPlanReference,
} from '@practium/features/api';
import { useToast, type ConfirmOptions } from '@practium/ui';
import type { PlanGeneralForm } from '../schemas/parameters.schema';
import type { FertilizationPlanReferenceDTO, PlanSectionKey, PlanUpdate } from '../types/parameters.types';
import { buildPlanPayload } from '../utils/referenceForm';

type Confirm = (options: ConfirmOptions) => Promise<boolean>;

export const usePlanReferencePage = (enabled: boolean, confirm: Confirm) => {
	const toast = useToast();
	const { data: plans = [], isLoading, isError } = useGetFertilizationPlanReferences({ enabled });
	const createMutation = useCreateFertilizationPlanReference();
	const updateMutation = useUpdateFertilizationPlanReference();
	const deleteMutation = useDeleteFertilizationPlanReference();
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [section, setSection] = useState<PlanSectionKey>('root');

	const activePlans = useMemo(() => plans.filter((item) => item.isActive), [plans]);
	const plan = plans.find((item) => item._id === selectedId) ?? activePlans[0] ?? plans[0] ?? null;

	const setPlansActive = useCallback(
		(targets: FertilizationPlanReferenceDTO[], isActive: boolean) =>
			Promise.all(targets.map((item) => updateMutation.mutateAsync({ id: item._id, data: { isActive } }))),
		[updateMutation]
	);

	const savePlan = useCallback(
		async (update: PlanUpdate, message: string) => {
			if (!plan) return false;
			try {
				await updateMutation.mutateAsync({ id: plan._id, data: update });
				toast.success(message);
				return true;
			} catch (error) {
				toast.error(getApiErrorMessage(error, 'Erro ao salvar. Tente novamente.'));
				return false;
			}
		},
		[plan, toast, updateMutation]
	);

	const createPlan = useCallback(
		async (form: PlanGeneralForm, base: FertilizationPlanReferenceDTO) => {
			try {
				await setPlansActive(activePlans, false);
			} catch (error) {
				toast.error(getApiErrorMessage(error, 'Erro ao desativar o plano atual. Nenhum plano foi criado.'));
				return false;
			}
			try {
				const created = await createMutation.mutateAsync(buildPlanPayload(base, form, plans));
				setSelectedId(created._id);
				toast.success('Plano criado e ativado com sucesso!');
				return true;
			} catch (error) {
				const message = getApiErrorMessage(error, 'Erro ao criar o plano.').replace(/\.?$/, '.');
				try {
					await setPlansActive(activePlans, true);
					toast.error(`${message} O plano anterior continua ativo.`);
				} catch {
					toast.error(`${message} O plano anterior ficou inativo: ative-o novamente na lista.`);
				}
				return false;
			}
		},
		[activePlans, createMutation, plans, setPlansActive, toast]
	);

	const activatePlan = useCallback(
		async (target: FertilizationPlanReferenceDTO) => {
			const confirmed = await confirm({
				title: 'Ativar plano',
				message: `O plano "${target.name}" passará a ser usado no cálculo de fertilização, e o plano ativo atual será desativado.`,
				confirmText: 'Ativar',
				cancelText: 'Cancelar',
				variant: 'warning',
			});
			if (!confirmed) return false;
			const previous = activePlans.filter((item) => item._id !== target._id);
			try {
				await setPlansActive(previous, false);
			} catch (error) {
				toast.error(getApiErrorMessage(error, 'Erro ao desativar o plano atual.'));
				return false;
			}
			try {
				await updateMutation.mutateAsync({ id: target._id, data: { isActive: true } });
				toast.success('Plano ativado com sucesso!');
				return true;
			} catch (error) {
				const message = getApiErrorMessage(error, 'Erro ao ativar o plano.').replace(/\.?$/, '.');
				try {
					await setPlansActive(previous, true);
					toast.error(`${message} O plano anterior continua ativo.`);
				} catch {
					toast.error(`${message} Nenhum plano ficou ativo: ative um plano na lista.`);
				}
				return false;
			}
		},
		[activePlans, confirm, setPlansActive, toast, updateMutation]
	);

	const deletePlan = useCallback(
		async (target: FertilizationPlanReferenceDTO) => {
			const confirmed = await confirm({
				title: 'Excluir plano',
				message: `Deseja excluir o plano "${target.name}"? Esta ação não pode ser desfeita.`,
				confirmText: 'Excluir',
				cancelText: 'Cancelar',
				variant: 'danger',
			});
			if (!confirmed) return false;
			try {
				await deleteMutation.mutateAsync(target._id);
				setSelectedId(null);
				toast.success('Plano excluído com sucesso!');
				return true;
			} catch (error) {
				toast.error(getApiErrorMessage(error, 'Erro ao excluir o plano.'));
				return false;
			}
		},
		[confirm, deleteMutation, toast]
	);

	return {
		plans,
		plan,
		section,
		isLoading,
		isError,
		isSaving: updateMutation.isPending || createMutation.isPending,
		setSection,
		selectPlan: setSelectedId,
		savePlan,
		createPlan,
		activatePlan,
		deletePlan,
	};
};
